import { useEffect, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import TvCard from '@/components/feature/TvCard';
import SEO from '@/components/base/SEO';
import { tvSeries, categories, siteInfo } from '@/mocks/tvSeries';

export default function Category() {
  const { category } = useParams<{ category: string }>();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const [activeCat, setActiveCat] = useState(category || 'all');

  useEffect(() => {
    if (category) {
      setActiveCat(category);
    }
  }, [category]);

  const filteredShows = activeCat === 'all'
    ? tvSeries
    : tvSeries.filter(s => s.categorySlug === activeCat);

  const currentCat = categories.find(c => c.slug === activeCat);
  const catLabel = currentCat ? currentCat.name : '全部';

  const seoTitle = catLabel === '全部'
    ? `${siteInfo.name} - 全部剧集 | 电视剧全集在线观看`
    : `${catLabel} - ${siteInfo.name} | ${catLabel}全集在线观看高清不卡`;

  const seoDesc = catLabel === '全部'
    ? `${siteInfo.name}提供${categories.filter(c => c.slug !== 'all').map(c => c.name).join('、')}等类型电视剧全集，高清不卡即点即看。`
    : `${siteInfo.name}为您提供最新${catLabel}资源，${catLabel}全集在线观看，高清画质流畅不卡顿，每日更新热门${catLabel}。`;

  const pageUrl = `https://caomin.tv/category/${activeCat}`;

  const categorySchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: 'https://caomin.tv',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: catLabel,
            item: pageUrl,
          },
        ],
      },
      {
        '@type': 'CollectionPage',
        name: seoTitle,
        description: seoDesc,
        url: pageUrl,
        mainEntity: {
          '@type': 'ItemList',
          numberOfItems: filteredShows.length,
          itemListElement: filteredShows.map((show, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            item: {
              '@type': 'TVSeries',
              name: show.title,
              url: `https://caomin.tv/article/${show.id}`,
            },
          })),
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SEO
        title={seoTitle}
        description={seoDesc}
        keywords={`${catLabel},${catLabel}全集,${catLabel}在线观看,电视剧全集,最新电视剧,草民影院`}
        canonicalUrl={pageUrl}
        schema={categorySchema}
      />

      <Navbar />

      <main className="pt-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: catLabel },
            ]}
          />

          <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
            {catLabel}
          </h1>
          <p className="text-sm text-foreground-600 mb-6">
            共 {filteredShows.length} 部剧集
          </p>

          <div className="flex items-center gap-1 overflow-x-auto flex-wrap mb-8 p-1 bg-background-100 rounded-xl">
            {categories.map(cat => (
              <Link
                key={cat.slug}
                to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
                className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-colors cursor-pointer ${
                  activeCat === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredShows.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                <i className="ri-movie-2-line text-2xl"></i>
              </div>
              <p className="text-foreground-600">暂无该分类的剧集</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5 pb-14">
              {filteredShows.map(show => (
                <TvCard key={show.id} {...show} />
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}